import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Card, CardHeader, CardContent } from './ui/Card';
import { formatCurrency, futureDate, getAmountMasked } from '../utils/currency';
import { Currency, LiquidityEvent, MacroShock } from '../types';
import { Wallet, Info, LineChart, BarChart3, Table2 } from 'lucide-react';
import { YearData } from '../utils/projection';
function getAgeAtYear(dob: string, yearsFromNow: number): number | null {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return null;
  const now = new Date();
  const currentAge = now.getFullYear() - birthDate.getFullYear();
  const hadBirthdayThisYear = now.getMonth() > birthDate.getMonth() || now.getMonth() === birthDate.getMonth() && now.getDate() >= birthDate.getDate();
  return (hadBirthdayThisYear ? currentAge : currentAge - 1) + yearsFromNow;
}
function AgeLabel({
  partnerNames,
  partnerDOBs,
  yearsFromNow,
  className = ''
}: {
  partnerNames: [string, string];
  partnerDOBs: [string, string];
  yearsFromNow: number;
  className?: string;
}) {
  const dobs = partnerDOBs || ['', ''];
  const age1 = getAgeAtYear(dobs[0], yearsFromNow);
  const age2 = getAgeAtYear(dobs[1], yearsFromNow);
  if (age1 === null && age2 === null) return null;
  return <p className={`text-xs ${className}`} data-id="element-579">
      {age1 !== null && <span data-id="element-580">
          {partnerNames[0]}: {age1}
        </span>}
      {age1 !== null && age2 !== null && <span data-id="element-581"> · </span>}
      {age2 !== null && <span data-id="element-582">
          {partnerNames[1]}: {age2}
        </span>}
    </p>;
}
interface InvestmentWidgetProps {
  baseCurrency: Currency;
  totalLiquid: number;
  totalIlliquid: number;
  projectionData: YearData[];
  retirementYear: number | null;
  partnerNames: [string, string];
  partnerDOBs: [string, string];
  liquidityEvents?: LiquidityEvent[];
  macroShocks?: MacroShock[];
}
export function InvestmentWidget({
  baseCurrency,
  totalLiquid,
  totalIlliquid,
  projectionData,
  retirementYear,
  partnerNames,
  partnerDOBs,
  liquidityEvents = [],
  macroShocks = []
}: InvestmentWidgetProps) {
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const hasHoldings = totalLiquid > 0 || totalIlliquid > 0;
  const hasData = projectionData.length > 1;
  const peakData = useMemo(() => {
    let peak = projectionData[0];
    for (const d of projectionData) {
      if (d.total > peak.total) peak = d;
    }
    return peak;
  }, [projectionData]);
  const finalData = projectionData[projectionData.length - 1];
  const retirementData = retirementYear !== null ? projectionData.find(d => d.year === retirementYear) : null;
  const chartData = useMemo(() => {
    return projectionData.map(d => ({
      year: d.label,
      total: d.total,
      contributed: d.contributed,
      date: futureDate(d.year)
    }));
  }, [projectionData]);
  // Build event markers for the chart — distinguish loan events
  const eventMarkers = useMemo(() => {
    if (!liquidityEvents.length) return [];
    const byYear: Record<number, LiquidityEvent[]> = {};
    for (const event of liquidityEvents) {
      const yr = event.startYear;
      if (!byYear[yr]) byYear[yr] = [];
      byYear[yr].push(event);
    }
    return Object.entries(byYear).map(([yearStr, events]) => {
      const year = Number(yearStr);
      const hasLoan = events.some(e => e.id.startsWith('loan-'));
      const hasRegular = events.some(e => !e.id.startsWith('loan-'));
      const labels = events.map(e => {
        const amount = formatCurrency(Math.abs(e.amount), e.currency);
        const sign = e.amount >= 0 ? '+' : '-';
        const suffix = e.type === 'monthly' ? '/mo' : e.type === 'yearly' ? '/yr' : '';
        const recur = e.type !== 'one-time' ? ` (${e.type}${e.endYear ? ` → Yr ${e.endYear}` : ''})` : '';
        return `${e.name}: ${sign}${amount}${suffix}${recur}`;
      });
      // Color logic: loan = orange, positive = purple, negative = red, mixed = slate
      let color: 'loan' | 'positive' | 'negative' | 'mixed' = 'positive';
      if (hasLoan && !hasRegular) {
        color = 'loan';
      } else if (hasLoan && hasRegular) {
        color = 'mixed';
      } else if (events.every(e => e.amount >= 0)) {
        color = 'positive';
      } else {
        color = 'negative';
      }
      return {
        year,
        xKey: `Yr ${year}`,
        label: labels.join(' · '),
        color
      };
    });
  }, [liquidityEvents, baseCurrency]);
  // Build shock markers for the chart
  const shockMarkers = useMemo(() => {
    return macroShocks.filter(s => s.isActive ?? true).map(s => {
      const impacts: string[] = [];
      if (s.liquidImpact !== 0) impacts.push(`Liq: ${s.liquidImpact > 0 ? '+' : ''}${s.liquidImpact}%`);
      if (s.illiquidImpact !== 0) impacts.push(`Illiq: ${s.illiquidImpact > 0 ? '+' : ''}${s.illiquidImpact}%`);
      if (s.realEstateImpact !== 0) impacts.push(`RE: ${s.realEstateImpact > 0 ? '+' : ''}${s.realEstateImpact}%`);
      return {
        year: s.year,
        xKey: `Yr ${s.year}`,
        label: `⚡ ${s.name}`,
        detail: impacts.join(', ')
      };
    });
  }, [macroShocks]);
  // Build table data with event annotations per year
  const tableData = useMemo(() => {
    if (!hasData) return [];
    // Pre-compute annual event impact per year
    const eventsByYear: Record<number, {
      name: string;
      annualImpact: number;
      currency: Currency;
      type: string;
      isLoan: boolean;
    }[]> = {};
    for (const event of liquidityEvents) {
      const start = event.startYear;
      const end = event.endYear ?? (event.type === 'one-time' ? start : projectionData[projectionData.length - 1]?.year ?? 30);
      for (let yr = start; yr <= end; yr++) {
        if (!eventsByYear[yr]) eventsByYear[yr] = [];
        // Apply annual increase for recurring events
        const rate = event.annualIncreaseRate ?? 0;
        const yearsOfGrowth = yr - start;
        const grownAmount = rate > 0 && event.type !== 'one-time' ? event.amount * Math.pow(1 + rate / 100, yearsOfGrowth) : event.amount;
        let annualImpact = 0;
        if (event.type === 'monthly') annualImpact = grownAmount * 12;else if (event.type === 'yearly') annualImpact = grownAmount;else annualImpact = grownAmount; // one-time
        eventsByYear[yr].push({
          name: event.name,
          annualImpact,
          currency: event.currency,
          type: event.type,
          isLoan: event.id.startsWith('loan-')
        });
      }
    }
    return projectionData.map(d => {
      const events = eventsByYear[d.year] || [];
      const totalEventImpact = events.reduce((sum, e) => sum + e.annualImpact, 0);
      return {
        ...d,
        date: futureDate(d.year),
        events,
        totalEventImpact
      };
    });
  }, [projectionData, liquidityEvents, hasData]);
  // Build stat cards data
  const statCards = useMemo(() => {
    if (!hasData) return [];
    const cards: {
      label: string;
      value: string;
      sub?: string;
      ageLine?: React.ReactNode;
      highlight?: boolean;
    }[] = [];
    // Starting balance
    cards.push({
      label: 'Starting Balance',
      value: formatCurrency(totalLiquid + totalIlliquid, baseCurrency),
      sub: `${formatCurrency(totalLiquid, baseCurrency)} liquid · ${formatCurrency(totalIlliquid, baseCurrency)} illiquid`
    });
    // At retirement (if applicable)
    if (retirementData && retirementYear !== null) {
      cards.push({
        label: `At Retirement (Yr ${retirementYear})`,
        value: formatCurrency(retirementData.total, baseCurrency),
        sub: futureDate(retirementYear),
        highlight: true,
        ageLine: <AgeLabel partnerNames={partnerNames} partnerDOBs={partnerDOBs} yearsFromNow={retirementYear} className="text-amber-500" data-id="element-583" />
      });
    }
    // Peak (only show if different from retirement year)
    if (peakData.year !== retirementYear) {
      cards.push({
        label: `Peak (Yr ${peakData.year})`,
        value: formatCurrency(peakData.total, baseCurrency),
        sub: futureDate(peakData.year),
        ageLine: <AgeLabel partnerNames={partnerNames} partnerDOBs={partnerDOBs} yearsFromNow={peakData.year} className="text-slate-400" data-id="element-584" />
      });
    }
    // End of horizon
    cards.push({
      label: `End of Horizon (Yr ${finalData.year})`,
      value: formatCurrency(finalData.total, baseCurrency),
      sub: `${futureDate(finalData.year)} · Contributed: ${formatCurrency(finalData.contributed, baseCurrency)}${finalData.withdrawn > 0 ? ` · Withdrawn: ${formatCurrency(finalData.withdrawn, baseCurrency)}` : ''}`,
      ageLine: <AgeLabel partnerNames={partnerNames} partnerDOBs={partnerDOBs} yearsFromNow={finalData.year} className="text-slate-400" data-id="element-585" />
    });
    return cards;
  }, [hasData, totalLiquid, totalIlliquid, baseCurrency, retirementData, retirementYear, peakData, finalData, partnerNames, partnerDOBs]);
  const eventColorMap = {
    loan: {
      stroke: '#ea580c',
      fill: '#c2410c',
      label: '#9a3412'
    },
    positive: {
      stroke: '#8b5cf6',
      fill: '#7c3aed',
      label: '#7c3aed'
    },
    negative: {
      stroke: '#ef4444',
      fill: '#dc2626',
      label: '#dc2626'
    },
    mixed: {
      stroke: '#64748b',
      fill: '#475569',
      label: '#475569'
    }
  };
  return <Card data-id="element-586">
      <CardHeader title="Net Worth Projection" subtitle="Accumulation, growth, and retirement trajectory" icon={<div className="bg-blue-600 p-2 rounded-lg shadow-sm" data-id="element-588">
            <LineChart className="w-4 h-4 text-white" data-id="element-589" />
          </div>} action={hasData ? <div className="flex bg-slate-100 p-0.5 rounded-lg" data-id="element-590">
              <button onClick={() => setViewMode('chart')} className={`flex items-center gap-1.5 py-1 px-2.5 text-xs font-medium rounded-md transition-all ${viewMode === 'chart' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-591">
                <BarChart3 className="w-3.5 h-3.5" data-id="element-592" />
                Chart
              </button>
              <button onClick={() => setViewMode('table')} className={`flex items-center gap-1.5 py-1 px-2.5 text-xs font-medium rounded-md transition-all ${viewMode === 'table' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-593">
                <Table2 className="w-3.5 h-3.5" data-id="element-594" />
                Table
              </button>
            </div> : undefined} data-id="element-587" />
      <CardContent className="space-y-5" data-id="element-595">
        {!hasHoldings && !hasData && <div className="py-12 flex items-center justify-center text-slate-400 text-sm" data-id="element-596">
            <div className="text-center" data-id="element-597">
              <Wallet className="w-10 h-10 mx-auto mb-3 text-slate-300" data-id="element-598" />
              <p data-id="element-599">No data to project yet.</p>
              <p className="text-xs mt-1" data-id="element-600">
                Add holdings or set a monthly contribution.
              </p>
            </div>
          </div>}

        {hasData && <>
            {/* Stat cards row */}
            <div className={`grid gap-4 ${statCards.length <= 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`} data-id="element-601">
              {statCards.map((card, i) => <div key={i} className={`rounded-lg p-3 border ${card.highlight ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'}`} data-id="element-602">
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${card.highlight ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-603">
                    {card.label}
                  </p>
                  <p className={`text-lg font-bold ${card.highlight ? 'text-amber-700' : 'text-slate-800'}`} data-id="element-604">
                    {card.value}
                  </p>
                  {card.sub && <p className={`text-xs mt-0.5 ${card.highlight ? 'text-amber-500' : 'text-slate-400'}`} data-id="element-605">
                      {card.sub}
                    </p>}
                  {card.ageLine}
                </div>)}
            </div>

            {/* Chart View */}
            {viewMode === 'chart' && <div className="h-[380px]" data-id="element-606">
                <ResponsiveContainer width="100%" height="100%" data-id="element-607">
                  <AreaChart data={chartData} margin={{
              top: 10,
              right: 16,
              left: 0,
              bottom: 0
            }} data-id="element-608">
                    <defs data-id="element-609">
                      <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1" data-id="element-610">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.12} data-id="element-611" />
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0} data-id="element-612" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-613" />
                    <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#94a3b8'
              }} dy={8} interval={Math.max(0, Math.floor(chartData.length / 10) - 1)} data-id="element-614" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#94a3b8'
              }} tickFormatter={v => getAmountMasked() ? '•••' : v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`} dx={-5} width={55} data-id="element-615" />
                    <Tooltip formatter={(value: number, name: string) => [formatCurrency(value, baseCurrency), name]} labelFormatter={(label: string, payload: any[]) => {
                const date = payload?.[0]?.payload?.date;
                return date ? `${label} — ${date}` : label;
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgb(0 0 0 / 0.08)',
                fontSize: '12px',
                padding: '8px 12px'
              }} labelStyle={{
                fontWeight: 600,
                marginBottom: 4
              }} data-id="element-616" />

                    {retirementYear !== null && <ReferenceLine x={`Yr ${retirementYear}`} stroke="#d97706" strokeWidth={2.5} label={{
                value: '🔥 Retire',
                position: 'insideTopLeft',
                fill: '#92400e',
                fontSize: 11,
                fontWeight: 600,
                offset: 8
              }} data-id="element-617" />}

                    {eventMarkers.map(marker => {
                const colors = eventColorMap[marker.color];
                return <ReferenceLine key={`event-${marker.year}`} x={marker.xKey} stroke={colors.stroke} strokeWidth={marker.color === 'loan' ? 1.5 : 1} strokeDasharray={marker.color === 'loan' ? '6 3' : '4 3'} label={{
                  value: marker.label,
                  position: 'insideBottomRight',
                  fill: colors.label,
                  fontSize: 9,
                  fontWeight: 500,
                  offset: 10
                }} data-id="element-618" />;
              })}

                    {shockMarkers.map(marker => <ReferenceLine key={`shock-${marker.year}`} x={marker.xKey} stroke="#dc2626" strokeWidth={2} strokeDasharray="3 3" label={{
                value: marker.label,
                position: 'insideTopRight',
                fill: '#991b1b',
                fontSize: 10,
                fontWeight: 600,
                offset: 8
              }} data-id="element-619" />)}

                    <Area type="monotone" dataKey="contributed" name="Contributed" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 4" fillOpacity={0} dot={false} data-id="element-620" />
                    <Area type="monotone" dataKey="total" name="Net Worth" stroke="#2563eb" strokeWidth={2} fillOpacity={1} fill="url(#gradTotal)" dot={false} data-id="element-621" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>}

            {/* Table View */}
            {viewMode === 'table' && <div className="overflow-x-auto rounded-lg border border-slate-200" data-id="element-622">
                <table className="w-full text-sm text-left" data-id="element-623">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200 sticky top-0" data-id="element-624">
                    <tr data-id="element-625">
                      <th className="px-4 py-2.5 font-medium" data-id="element-626">Year</th>
                      <th className="px-4 py-2.5 font-medium" data-id="element-627">Date</th>
                      <th className="px-4 py-2.5 font-medium" data-id="element-628">Phase</th>
                      <th className="px-4 py-2.5 font-medium text-right" data-id="element-629">
                        Liquid
                      </th>
                      <th className="px-4 py-2.5 font-medium text-right" data-id="element-630">
                        Illiquid
                      </th>
                      <th className="px-4 py-2.5 font-medium text-right" data-id="element-631">
                        Net Worth
                      </th>
                      <th className="px-4 py-2.5 font-medium text-right" data-id="element-632">
                        Contributed
                      </th>
                      <th className="px-4 py-2.5 font-medium text-right" data-id="element-633">
                        Withdrawn
                      </th>
                      <th className="px-4 py-2.5 font-medium" data-id="element-634">
                        Events & Loans
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-635">
                    {tableData.map(row => {
                const isRetirement = retirementYear !== null && row.year === retirementYear;
                const isDepleted = row.depleted;
                return <tr key={row.year} className={`${isRetirement ? 'bg-amber-50/60' : isDepleted ? 'bg-red-50/40' : 'hover:bg-slate-50/50'} transition-colors`} data-id="element-636">
                          <td className="px-4 py-2.5 font-medium text-slate-800" data-id="element-637">
                            {row.label}
                            {isRetirement && <span className="ml-1.5 text-amber-600" data-id="element-638">🔥</span>}
                          </td>
                          <td className="px-4 py-2.5 text-slate-500 text-xs" data-id="element-639">
                            {row.date}
                          </td>
                          <td className="px-4 py-2.5" data-id="element-640">
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${row.phase === 'accumulation' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-641">
                              {row.phase === 'accumulation' ? 'Accum.' : 'Drawdown'}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums" data-id="element-642">
                            {row.liquid > 0 ? formatCurrency(row.liquid, baseCurrency) : '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-700 tabular-nums" data-id="element-643">
                            {row.illiquid > 0 ? formatCurrency(row.illiquid, baseCurrency) : '—'}
                          </td>
                          <td className={`px-4 py-2.5 text-right font-semibold tabular-nums ${isDepleted ? 'text-red-600' : 'text-slate-900'}`} data-id="element-644">
                            {formatCurrency(row.total, baseCurrency)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-500 tabular-nums" data-id="element-645">
                            {formatCurrency(row.contributed, baseCurrency)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-slate-500 tabular-nums" data-id="element-646">
                            {row.withdrawn > 0 ? formatCurrency(row.withdrawn, baseCurrency) : '—'}
                          </td>
                          <td className="px-4 py-2.5 max-w-[260px]" data-id="element-647">
                            {row.events.length > 0 ? <div className="space-y-0.5" data-id="element-648">
                                {row.events.map((evt, i) => <div key={i} className="flex items-center gap-1.5 text-xs" data-id="element-649">
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${evt.isLoan ? 'bg-orange-500' : evt.annualImpact >= 0 ? 'bg-violet-500' : 'bg-red-500'}`} data-id="element-650" />
                                    <span className={`truncate ${evt.isLoan ? 'text-orange-700' : evt.annualImpact >= 0 ? 'text-violet-700' : 'text-red-600'}`} data-id="element-651">
                                      {evt.name}
                                    </span>
                                    <span className={`shrink-0 font-medium ${evt.annualImpact >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-652">
                                      {evt.annualImpact >= 0 ? '+' : ''}
                                      {formatCurrency(evt.annualImpact, evt.currency)}
                                      /{evt.type === 'one-time' ? 'once' : 'yr'}
                                    </span>
                                  </div>)}
                              </div> : null}
                            {shockMarkers.filter(m => m.year === row.year).map((m, i) => <div key={`shock-${i}`} className="flex items-center gap-1.5 text-xs mt-0.5" data-id="element-653">
                                  <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-red-600" data-id="element-654" />
                                  <span className="text-red-700 font-medium truncate" data-id="element-655">
                                    ⚡ {m.label.replace('⚡ ', '')}
                                  </span>
                                  <span className="text-red-500 shrink-0" data-id="element-656">
                                    {m.detail}
                                  </span>
                                </div>)}
                            {row.events.length === 0 && !shockMarkers.some(m => m.year === row.year) && <span className="text-slate-300 text-xs" data-id="element-657">
                                  —
                                </span>}
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div>}
          </>}
      </CardContent>
    </Card>;
}